import {
  http,
  delay,
  HttpResponse,
} from 'msw';

import { createMockEndpoint } from '@/mocks/create-mock-endpoint';
import {
  ExternalTransactionHistoryInfoList,
  TransactionBasicInfo,
  TransactionDetailResponse,
} from '@/repositories/generated';

// 取引履歴一覧モック（transaction.ts）の全transactionIdと整合させた取引詳細モック。
// 一覧の各行から遷移した際にその取引の詳細が表示される（未知のIDはdefaultResponseにフォールバック）。
// 取引詳細区分ごとのカード出し分け・金額設定規則（商品券決済は取引金額0円＋調整額、
// 残高調整・失効は調整額なし＋加盟店情報なし、外部取引履歴は外部連携が発生する区分のみ等）は
// 取引履歴詳細画面_改修仕様書 3章・6章・9章のマトリクスに準拠

// 基本情報パターンA：コード決済系（加盟店情報あり）
const merchantBasicInfo: TransactionBasicInfo = {
  accountId: undefined,
  acquirerId: 7,
  acquirerName: 'テストアクワイアラ3-1',
  codeType: 'MPM',
  companyId: '10005',
  companyName: '株式会社IC商店',
  externalAcquirerId: 'Acquirer_7',
  externalCompanyId: 'K000002T00000M005',
  externalShopId: 'K000002T00001M000',
  externalTerminalId: 'K000002T00001M001',
  externalUserId: '5352f9df41c14ec7bdd8e58b10d30b1a',
  issuerId: '9',
  issuerName: '株式会社肥後銀行テスト9',
  receiptId: '123456789',
  shopId: '90000001',
  shopName: 'IC商店阿蘇店',
  shopPhone: '0999-99-9999',
  terminalId: '0000003506179',
  terminalName: 'IC商店阿蘇店レジ1',
  transactionDateTime: '2026-06-08T18:48:39+09:00',
  walletServiceId: '101',
  walletServiceName: 'くまモン!Pay101',
  walletUserId: '658',
};

// 基本情報パターンB：商品券購入・残高調整・失効（加盟店情報なし）
const nonMerchantBasicInfo: TransactionBasicInfo = {
  externalUserId: '5352f9df41c14ec7bdd8e58b10d30b1a',
  issuerId: '9',
  issuerName: '株式会社肥後銀行テスト9',
  transactionDateTime: '2026-06-08T18:46:55+09:00',
  walletServiceId: '101',
  walletServiceName: 'くまモン!Pay101',
  walletUserId: '658',
};

// 商品券事業者・事業（ABI用）
const abiGiftFields = {
  giftIssuerId: '3',
  giftIssuerName: 'ABI用商品券事業者1',
  giftBusinessId: '7',
  giftBusinessName: 'ABI用商品券事業01',
};

// 外部連携（MKPへの送受信）が発生しない取引（商品券のみ決済・残高調整・失効等）は空
const emptyExternalTransactionHistoryList: ExternalTransactionHistoryInfoList = {
  externalTransactionHistoryInfo: [],
};

const createExternalTransactionHistoryList = (transactionId: string): ExternalTransactionHistoryInfoList => ({
  externalTransactionHistoryInfo: [
    {
      transactionId,
      transactionIdSeq: 1,
      executeApi: '/v2/payment',
      httpStatus: '200',
      sendResult: 0,
      request: '{"transactionId":"xxxx","amount":1000}',
      response: '{"resultCode":"0000"}',
      createdAt: '2026-06-08T18:48:39+09:00',
    },
  ],
});

// デフォルト：コード決済 / ハウスプリペイド決済（商品券なし）
const defaultResponse: TransactionDetailResponse = {
  adjustmentAmount: 0,
  cancelFlg: true,
  externalTransactionHistoryList: createExternalTransactionHistoryList('260608185500699864'),
  giftInfo: {},
  giftPurchaseInfo: {
    giftTicketList: [],
  },
  serviceTransactionId: '20260608000000000000000000000006',
  specifiedAmount: 2200,
  status: 'SUCCESS',
  transactionAmount: 2200,
  transactionBasicInfo: {
    ...merchantBasicInfo,
    transactionDateTime: '2026-06-08T00:00:00+09:00',
  },
  transactionCancelInfo: {},
  transactionDetailType: 'HOUSE_PREPAID_SETTLEMENT',
  transactionId: '260608185500699864',
  transactionType: 'CODE_PAYMENT',
};

const responses: Record<string, TransactionDetailResponse> = {
  // コード決済 / 商品券決済（全額商品券払いのため取引金額0円・外部連携なし）
  '260608184839701667': {
    adjustmentAmount: 800,
    cancelFlg: true,
    externalTransactionHistoryList: emptyExternalTransactionHistoryList,
    giftInfo: {
      bonusValueTransactionAmount: 250,
      giftTicketId: '13',
      giftTicketName: 'ABI用商品券面01-01',
      prepaidValueTransactionAmount: 550,
    },
    giftPurchaseInfo: {
      giftTicketList: [],
    },
    serviceTransactionId: '20260608000000000000000000000001',
    specifiedAmount: 800,
    status: 'SUCCESS',
    transactionAmount: 0,
    transactionBasicInfo: {
      ...merchantBasicInfo,
      ...abiGiftFields,
      transactionDateTime: '2026-06-08T18:48:39+09:00',
    },
    transactionCancelInfo: {},
    transactionDetailType: 'GIFT_CERTIFICATE_SETTLEMENT',
    transactionId: '260608184839701667',
    transactionType: 'CODE_PAYMENT',
  },
  // コード決済 / 商品券併用決済（取引金額＝ハウスプリペイド分・調整額＝商品券分）
  '260608185037108826': {
    adjustmentAmount: 450,
    cancelFlg: true,
    externalTransactionHistoryList: createExternalTransactionHistoryList('260608185037108826'),
    giftInfo: {
      bonusValueTransactionAmount: 0,
      giftTicketId: '13',
      giftTicketName: 'ABI用商品券面01-01',
      prepaidValueTransactionAmount: 450,
    },
    giftPurchaseInfo: {
      giftTicketList: [],
    },
    serviceTransactionId: '20260608000000000000000000000002',
    specifiedAmount: 1500,
    status: 'SUCCESS',
    transactionAmount: 1050,
    transactionBasicInfo: {
      ...merchantBasicInfo,
      ...abiGiftFields,
      transactionDateTime: '2026-06-08T18:50:37+09:00',
    },
    transactionCancelInfo: {},
    transactionDetailType: 'SPLIT_SETTLEMENT',
    transactionId: '260608185037108826',
    transactionType: 'CODE_PAYMENT',
  },
  // コード決済 / 商品券決済（取消済みのため取消ボタン非表示）
  '260609111429647715': {
    adjustmentAmount: 900,
    cancelFlg: false,
    externalTransactionHistoryList: emptyExternalTransactionHistoryList,
    giftInfo: {
      bonusValueTransactionAmount: 250,
      giftTicketId: '13',
      giftTicketName: 'ABI用商品券面01-01',
      prepaidValueTransactionAmount: 650,
    },
    giftPurchaseInfo: {
      giftTicketList: [],
    },
    serviceTransactionId: '20260609000000000000000000000006',
    specifiedAmount: 900,
    status: 'CANCELED',
    transactionAmount: 0,
    transactionBasicInfo: {
      ...merchantBasicInfo,
      ...abiGiftFields,
      transactionDateTime: '2026-06-08T11:14:29+09:00',
    },
    transactionCancelInfo: {},
    transactionDetailType: 'GIFT_CERTIFICATE_SETTLEMENT',
    transactionId: '260609111429647715',
    transactionType: 'CODE_PAYMENT',
  },
  // コード決済取消 / 商品券決済取消（取消情報カードあり・外部連携なし）
  '260609112633503051': {
    adjustmentAmount: 900,
    cancelFlg: false,
    externalTransactionHistoryList: emptyExternalTransactionHistoryList,
    giftInfo: {
      bonusValueTransactionAmount: 250,
      giftTicketId: '13',
      giftTicketName: 'ABI用商品券面01-01',
      prepaidValueTransactionAmount: 650,
    },
    giftPurchaseInfo: {
      giftTicketList: [],
    },
    serviceTransactionId: undefined,
    specifiedAmount: 900,
    status: 'SUCCESS',
    transactionAmount: 0,
    transactionBasicInfo: {
      ...merchantBasicInfo,
      ...abiGiftFields,
      transactionDateTime: '2026-06-08T11:26:33+09:00',
    },
    transactionCancelInfo: {
      canceledReceiptId: '123456789',
      canceledServiceTransactionId: '20260609000000000000000000000006',
      canceledTransactionDateTime: '2026-06-08T11:14:29+09:00',
      canceledTransactionId: '260609111429647715',
    },
    transactionDetailType: 'GIFT_CERTIFICATE_SETTLEMENT_CANCEL',
    transactionId: '260609112633503051',
    transactionType: 'CODE_PAYMENT_CANCEL',
  },
  // コード決済取消 / 商品券併用決済取消（取消情報カードあり・外部連携あり）
  '260609112555653304': {
    adjustmentAmount: 350,
    cancelFlg: false,
    externalTransactionHistoryList: createExternalTransactionHistoryList('260609112555653304'),
    giftInfo: {
      bonusValueTransactionAmount: 0,
      giftTicketId: '13',
      giftTicketName: 'ABI用商品券面01-01',
      prepaidValueTransactionAmount: 350,
    },
    giftPurchaseInfo: {
      giftTicketList: [],
    },
    serviceTransactionId: undefined,
    specifiedAmount: 500,
    status: 'SUCCESS',
    transactionAmount: 150,
    transactionBasicInfo: {
      ...merchantBasicInfo,
      ...abiGiftFields,
      transactionDateTime: '2026-06-08T11:25:55+09:00',
    },
    transactionCancelInfo: {
      canceledReceiptId: '123456789',
      canceledServiceTransactionId: '20260609000000000000000000000007',
      canceledTransactionDateTime: '2026-06-08T11:15:07+09:00',
      canceledTransactionId: '260609111507235913',
    },
    transactionDetailType: 'SPLIT_SETTLEMENT_CANCEL',
    transactionId: '260609112555653304',
    transactionType: 'CODE_PAYMENT_CANCEL',
  },
  // 商品券購入 / ハウスプリペイド商品券購入（商品券購入カードあり・加盟店情報なし）
  '260608184655114894': {
    adjustmentAmount: 0,
    cancelFlg: false,
    externalTransactionHistoryList: createExternalTransactionHistoryList('260608184655114894'),
    giftInfo: {},
    giftPurchaseInfo: {
      expiresEndsAt: '2026-09-30T23:59:59+09:00',
      expiresStartsAt: '2026-04-01T00:00:00+09:00',
      giftSalesManagementId: '5',
      giftSalesManagementName: 'ABI用商品券販売管理01',
      giftTicketList: [
        {
          giftTicketId: '13',
          giftTicketName: 'ABI用商品券面01-01',
          giftTicketPerBonusValueAmount: 200,
          giftTicketPerPrepaidValueAmount: 1000,
          giftTicketPerTotalGrantedAmount: 1200,
        },
      ],
      premiumRate: 0.2,
      purchaseCount: 1,
      totalGrantedAmount: 1200,
    },
    serviceTransactionId: '20260413100000000000000000000010',
    specifiedAmount: 1000,
    status: 'SUCCESS',
    transactionAmount: 1000,
    transactionBasicInfo: {
      ...nonMerchantBasicInfo,
      ...abiGiftFields,
      transactionDateTime: '2026-06-08T18:46:55+09:00',
    },
    transactionCancelInfo: {},
    transactionDetailType: 'HOUSE_PREPAID_PURCHASE_GIFT',
    transactionId: '260608184655114894',
    transactionType: 'PURCHASE_GIFT_CERTIFICATE',
  },
  // 商品券残高調整(加算)（調整額なし・外部連携なし・加盟店情報なし）
  '600026060813095080': {
    adjustmentAmount: undefined,
    cancelFlg: false,
    externalTransactionHistoryList: emptyExternalTransactionHistoryList,
    giftInfo: {
      balanceReason: '入金誤り補填',
      bonusValueTransactionAmount: 0,
      expiresEndsAt: '2026-09-30T23:59:59+09:00',
      giftSalesManagementId: '5',
      giftSalesManagementName: 'ABI用商品券販売管理01',
      giftTicketId: '13',
      giftTicketName: 'ABI用商品券面01-01',
      prepaidValueTransactionAmount: 100,
    },
    giftPurchaseInfo: {
      giftTicketList: [],
    },
    serviceTransactionId: undefined,
    specifiedAmount: undefined,
    status: 'SUCCESS',
    transactionAmount: 100,
    transactionBasicInfo: {
      ...nonMerchantBasicInfo,
      ...abiGiftFields,
      transactionDateTime: '2026-06-08T13:09:50+09:00',
    },
    transactionCancelInfo: {},
    transactionDetailType: 'GIFT_BALANCE_ADJUSTMENT_IN',
    transactionId: '600026060813095080',
    transactionType: 'GIFT_BALANCE_ADJUSTMENT_IN',
  },
  // 商品券残高調整(減算)（商品券販売管理名・有効期限終了日時は「-」）
  '600026060813101291': {
    adjustmentAmount: undefined,
    cancelFlg: false,
    externalTransactionHistoryList: emptyExternalTransactionHistoryList,
    giftInfo: {
      balanceReason: '誤付与の取消',
      bonusValueTransactionAmount: 0,
      giftTicketId: '13',
      giftTicketName: 'ABI用商品券面01-01',
      prepaidValueTransactionAmount: 100,
    },
    giftPurchaseInfo: {
      giftTicketList: [],
    },
    serviceTransactionId: undefined,
    specifiedAmount: undefined,
    status: 'SUCCESS',
    transactionAmount: 100,
    transactionBasicInfo: {
      ...nonMerchantBasicInfo,
      ...abiGiftFields,
      transactionDateTime: '2026-06-08T13:10:12+09:00',
    },
    transactionCancelInfo: {},
    transactionDetailType: 'GIFT_BALANCE_ADJUSTMENT_OUT',
    transactionId: '600026060813101291',
    transactionType: 'GIFT_BALANCE_ADJUSTMENT_OUT',
  },
  // 商品券残高失効（失効理由あり・外部連携なし・加盟店情報なし）
  '500026060800000001': {
    adjustmentAmount: undefined,
    cancelFlg: false,
    externalTransactionHistoryList: emptyExternalTransactionHistoryList,
    giftInfo: {
      bonusValueTransactionAmount: 0,
      expiredReason: '有効期限切れ',
      expiresEndsAt: '2026-06-07T23:59:59+09:00',
      giftSalesManagementId: '1',
      giftSalesManagementName: '熊本市デジタル商品券販売管理01',
      giftTicketId: '1',
      giftTicketName: '共通利用券',
      prepaidValueTransactionAmount: 500,
    },
    giftPurchaseInfo: {
      giftTicketList: [],
    },
    serviceTransactionId: undefined,
    specifiedAmount: undefined,
    status: 'SUCCESS',
    transactionAmount: 500,
    transactionBasicInfo: {
      ...nonMerchantBasicInfo,
      giftIssuerId: '1',
      giftIssuerName: 'くまもとデジタル推進協会',
      giftBusinessId: '1',
      giftBusinessName: '熊本市デジタル商品券',
      transactionDateTime: '2026-06-08T10:00:00+09:00',
      walletUserId: '659',
      externalUserId: '5fb14db523874dacbcf54934b65dc276',
    },
    transactionCancelInfo: {},
    transactionDetailType: 'BALANCE_EXPIRED',
    transactionId: '500026060800000001',
    transactionType: 'GIFT_BALANCE_EXPIRED',
  },
  // コード決済 / ハウスプリペイド決済（取消済みの元取引・取消ボタン非表示）
  '260609110924597552': {
    adjustmentAmount: 0,
    cancelFlg: false,
    externalTransactionHistoryList: createExternalTransactionHistoryList('260609110924597552'),
    giftInfo: {},
    giftPurchaseInfo: {
      giftTicketList: [],
    },
    serviceTransactionId: '20260609000000000000000000000002',
    specifiedAmount: 3000,
    status: 'CANCELED',
    transactionAmount: 3000,
    transactionBasicInfo: {
      ...merchantBasicInfo,
      transactionDateTime: '2026-06-08T11:09:24+09:00',
    },
    transactionCancelInfo: {},
    transactionDetailType: 'HOUSE_PREPAID_SETTLEMENT',
    transactionId: '260609110924597552',
    transactionType: 'CODE_PAYMENT',
  },
  // コード決済 / 商品券併用決済（取消済みの元取引・取消ボタン非表示）
  '260609111507235913': {
    adjustmentAmount: 350,
    cancelFlg: false,
    externalTransactionHistoryList: createExternalTransactionHistoryList('260609111507235913'),
    giftInfo: {
      bonusValueTransactionAmount: 0,
      giftTicketId: '13',
      giftTicketName: 'ABI用商品券面01-01',
      prepaidValueTransactionAmount: 350,
    },
    giftPurchaseInfo: {
      giftTicketList: [],
    },
    serviceTransactionId: '20260609000000000000000000000007',
    specifiedAmount: 500,
    status: 'CANCELED',
    transactionAmount: 150,
    transactionBasicInfo: {
      ...merchantBasicInfo,
      ...abiGiftFields,
      transactionDateTime: '2026-06-08T11:15:07+09:00',
    },
    transactionCancelInfo: {},
    transactionDetailType: 'SPLIT_SETTLEMENT',
    transactionId: '260609111507235913',
    transactionType: 'CODE_PAYMENT',
  },
  // コード決済取消 / ハウスプリペイド決済取消（取消情報カードあり・外部連携あり）
  '260609112213988569': {
    adjustmentAmount: 0,
    cancelFlg: false,
    externalTransactionHistoryList: createExternalTransactionHistoryList('260609112213988569'),
    giftInfo: {},
    giftPurchaseInfo: {
      giftTicketList: [],
    },
    serviceTransactionId: undefined,
    specifiedAmount: 3000,
    status: 'SUCCESS',
    transactionAmount: 3000,
    transactionBasicInfo: {
      ...merchantBasicInfo,
      transactionDateTime: '2026-06-08T11:22:14+09:00',
    },
    transactionCancelInfo: {
      canceledReceiptId: '123456789',
      canceledServiceTransactionId: '20260609000000000000000000000002',
      canceledTransactionDateTime: '2026-06-08T11:09:24+09:00',
      canceledTransactionId: '260609110924597552',
    },
    transactionDetailType: 'HOUSE_PREPAID_SETTLEMENT_CANCEL',
    transactionId: '260609112213988569',
    transactionType: 'CODE_PAYMENT_CANCEL',
  },
  // 商品券残高調整(加算)（ボーナスバリュー側のみ）
  '600026060813105355': {
    adjustmentAmount: undefined,
    cancelFlg: false,
    externalTransactionHistoryList: emptyExternalTransactionHistoryList,
    giftInfo: {
      balanceReason: 'キャンペーン補填',
      bonusValueTransactionAmount: 100,
      expiresEndsAt: '2026-09-30T23:59:59+09:00',
      giftSalesManagementId: '5',
      giftSalesManagementName: 'ABI用商品券販売管理01',
      giftTicketId: '13',
      giftTicketName: 'ABI用商品券面01-01',
      prepaidValueTransactionAmount: 0,
    },
    giftPurchaseInfo: {
      giftTicketList: [],
    },
    serviceTransactionId: undefined,
    specifiedAmount: undefined,
    status: 'SUCCESS',
    transactionAmount: 100,
    transactionBasicInfo: {
      ...nonMerchantBasicInfo,
      ...abiGiftFields,
      transactionDateTime: '2026-06-08T13:10:53+09:00',
    },
    transactionCancelInfo: {},
    transactionDetailType: 'GIFT_BALANCE_ADJUSTMENT_IN',
    transactionId: '600026060813105355',
    transactionType: 'GIFT_BALANCE_ADJUSTMENT_IN',
  },
  // 商品券残高調整(減算)（ボーナスバリュー側のみ・販売管理/有効期限は「-」）
  '600026060813110399': {
    adjustmentAmount: undefined,
    cancelFlg: false,
    externalTransactionHistoryList: emptyExternalTransactionHistoryList,
    giftInfo: {
      balanceReason: '誤付与の取消',
      bonusValueTransactionAmount: 100,
      giftTicketId: '13',
      giftTicketName: 'ABI用商品券面01-01',
      prepaidValueTransactionAmount: 0,
    },
    giftPurchaseInfo: {
      giftTicketList: [],
    },
    serviceTransactionId: undefined,
    specifiedAmount: undefined,
    status: 'SUCCESS',
    transactionAmount: 100,
    transactionBasicInfo: {
      ...nonMerchantBasicInfo,
      ...abiGiftFields,
      transactionDateTime: '2026-06-08T13:11:04+09:00',
    },
    transactionCancelInfo: {},
    transactionDetailType: 'GIFT_BALANCE_ADJUSTMENT_OUT',
    transactionId: '600026060813110399',
    transactionType: 'GIFT_BALANCE_ADJUSTMENT_OUT',
  },
  // 商品券残高調整(加算)（別事業者・くまもとデジタル推進協会）
  '600026060815594777': {
    adjustmentAmount: undefined,
    cancelFlg: false,
    externalTransactionHistoryList: emptyExternalTransactionHistoryList,
    giftInfo: {
      balanceReason: '入金誤り補填',
      bonusValueTransactionAmount: 0,
      expiresEndsAt: '2026-12-31T23:59:59+09:00',
      giftSalesManagementId: '6',
      giftSalesManagementName: '自由枠販売管理A',
      giftTicketId: '3',
      giftTicketName: '自由枠券面A',
      prepaidValueTransactionAmount: 100,
    },
    giftPurchaseInfo: {
      giftTicketList: [],
    },
    serviceTransactionId: undefined,
    specifiedAmount: undefined,
    status: 'SUCCESS',
    transactionAmount: 100,
    transactionBasicInfo: {
      ...nonMerchantBasicInfo,
      externalUserId: '31a0f6794a964956b690ec50c85ec4f3',
      giftIssuerId: '1',
      giftIssuerName: 'くまもとデジタル推進協会',
      giftBusinessId: '4',
      giftBusinessName: '自由枠事業A',
      transactionDateTime: '2026-06-08T15:59:47+09:00',
      walletUserId: '671',
    },
    transactionCancelInfo: {},
    transactionDetailType: 'GIFT_BALANCE_ADJUSTMENT_IN',
    transactionId: '600026060815594777',
    transactionType: 'GIFT_BALANCE_ADJUSTMENT_IN',
  },
  // コード決済取消 / 商品券決済取消（高額・取消情報カードあり・外部連携なし）
  '260608181813738604': {
    adjustmentAmount: 12000,
    cancelFlg: false,
    externalTransactionHistoryList: emptyExternalTransactionHistoryList,
    giftInfo: {
      bonusValueTransactionAmount: 7000,
      giftTicketId: '3',
      giftTicketName: '自由枠券面A',
      prepaidValueTransactionAmount: 5000,
    },
    giftPurchaseInfo: {
      giftTicketList: [],
    },
    serviceTransactionId: undefined,
    specifiedAmount: 12000,
    status: 'SUCCESS',
    transactionAmount: 0,
    transactionBasicInfo: {
      ...merchantBasicInfo,
      externalUserId: 'f03bcc7b10774696ac07f6dd63fc0029',
      giftIssuerId: '1',
      giftIssuerName: 'くまもとデジタル推進協会',
      giftBusinessId: '4',
      giftBusinessName: '自由枠事業A',
      transactionDateTime: '2026-06-08T18:18:13+09:00',
      walletUserId: '681',
    },
    transactionCancelInfo: {
      canceledReceiptId: '123456789',
      canceledServiceTransactionId: '68130000000000000000000000000003',
      canceledTransactionDateTime: '2026-06-05T18:43:43+09:00',
      canceledTransactionId: '260605184343445090',
    },
    transactionDetailType: 'GIFT_CERTIFICATE_SETTLEMENT_CANCEL',
    transactionId: '260608181813738604',
    transactionType: 'CODE_PAYMENT_CANCEL',
  },
  // コード決済 / ハウスプリペイド決済（2,000円）
  '260608185415085230': {
    adjustmentAmount: 0,
    cancelFlg: true,
    externalTransactionHistoryList: createExternalTransactionHistoryList('260608185415085230'),
    giftInfo: {},
    giftPurchaseInfo: {
      giftTicketList: [],
    },
    serviceTransactionId: '20260608000000000000000000000004',
    specifiedAmount: 2000,
    status: 'SUCCESS',
    transactionAmount: 2000,
    transactionBasicInfo: {
      ...merchantBasicInfo,
      transactionDateTime: '2026-06-08T18:54:15+09:00',
    },
    transactionCancelInfo: {},
    transactionDetailType: 'HOUSE_PREPAID_SETTLEMENT',
    transactionId: '260608185415085230',
    transactionType: 'CODE_PAYMENT',
  },
  // コード決済 / ハウスプリペイド決済（2,100円）
  '260608185443360705': {
    adjustmentAmount: 0,
    cancelFlg: true,
    externalTransactionHistoryList: createExternalTransactionHistoryList('260608185443360705'),
    giftInfo: {},
    giftPurchaseInfo: {
      giftTicketList: [],
    },
    serviceTransactionId: '20260608000000000000000000000005',
    specifiedAmount: 2100,
    status: 'SUCCESS',
    transactionAmount: 2100,
    transactionBasicInfo: {
      ...merchantBasicInfo,
      transactionDateTime: '2026-06-08T18:54:43+09:00',
    },
    transactionCancelInfo: {},
    transactionDetailType: 'HOUSE_PREPAID_SETTLEMENT',
    transactionId: '260608185443360705',
    transactionType: 'CODE_PAYMENT',
  },
  // コード決済 / ハウスプリペイド決済（高額・日跨ぎ境界）
  '260608185613141583': {
    adjustmentAmount: 0,
    cancelFlg: true,
    externalTransactionHistoryList: createExternalTransactionHistoryList('260608185613141583'),
    giftInfo: {},
    giftPurchaseInfo: {
      giftTicketList: [],
    },
    serviceTransactionId: '20260608000000000000000000000007',
    specifiedAmount: 9999999,
    status: 'SUCCESS',
    transactionAmount: 9999999,
    transactionBasicInfo: {
      ...merchantBasicInfo,
      transactionDateTime: '2026-06-08T23:59:59+09:00',
    },
    transactionCancelInfo: {},
    transactionDetailType: 'HOUSE_PREPAID_SETTLEMENT',
    transactionId: '260608185613141583',
    transactionType: 'CODE_PAYMENT',
  },
  // セルフチャージ / BankPayチャージ（加盟店情報なし・調整額なし・外部連携あり）
  '260608083000000101': {
    adjustmentAmount: undefined,
    cancelFlg: false,
    externalTransactionHistoryList: createExternalTransactionHistoryList('260608083000000101'),
    giftInfo: {},
    giftPurchaseInfo: {
      giftTicketList: [],
    },
    serviceTransactionId: '20260608000000000000000000000101',
    specifiedAmount: undefined,
    status: 'SUCCESS',
    transactionAmount: 5000,
    transactionBasicInfo: {
      ...nonMerchantBasicInfo,
      transactionDateTime: '2026-06-08T08:30:00+09:00',
    },
    transactionCancelInfo: {},
    transactionDetailType: 'BANK_PAY_CHARGE',
    transactionId: '260608083000000101',
    transactionType: 'SELF_CHARGE',
  },
  // セルフチャージ / ポイントチャージ
  '260608083500000102': {
    adjustmentAmount: undefined,
    cancelFlg: false,
    externalTransactionHistoryList: createExternalTransactionHistoryList('260608083500000102'),
    giftInfo: {},
    giftPurchaseInfo: {
      giftTicketList: [],
    },
    serviceTransactionId: '20260608000000000000000000000102',
    specifiedAmount: undefined,
    status: 'SUCCESS',
    transactionAmount: 1000,
    transactionBasicInfo: {
      ...nonMerchantBasicInfo,
      externalUserId: '5fb14db523874dacbcf54934b65dc276',
      transactionDateTime: '2026-06-08T08:35:00+09:00',
      walletUserId: '659',
    },
    transactionCancelInfo: {},
    transactionDetailType: 'POINT_CHARGE',
    transactionId: '260608083500000102',
    transactionType: 'SELF_CHARGE',
  },
  // セルフチャージ / ApplePayチャージ
  '260608084000000103': {
    adjustmentAmount: undefined,
    cancelFlg: false,
    externalTransactionHistoryList: createExternalTransactionHistoryList('260608084000000103'),
    giftInfo: {},
    giftPurchaseInfo: {
      giftTicketList: [],
    },
    serviceTransactionId: '20260608000000000000000000000103',
    specifiedAmount: undefined,
    status: 'SUCCESS',
    transactionAmount: 3000,
    transactionBasicInfo: {
      ...nonMerchantBasicInfo,
      externalUserId: '31a0f6794a964956b690ec50c85ec4f3',
      transactionDateTime: '2026-06-08T08:40:00+09:00',
      walletUserId: '671',
    },
    transactionCancelInfo: {},
    transactionDetailType: 'APPLE_PAY_CHARGE',
    transactionId: '260608084000000103',
    transactionType: 'SELF_CHARGE',
  },
  // セルフチャージ / BankPayチャージ（認証待ち）
  '260608084500000104': {
    adjustmentAmount: undefined,
    cancelFlg: false,
    externalTransactionHistoryList: createExternalTransactionHistoryList('260608084500000104'),
    giftInfo: {},
    giftPurchaseInfo: {
      giftTicketList: [],
    },
    serviceTransactionId: '20260608000000000000000000000104',
    specifiedAmount: undefined,
    status: 'WAITING_AUTH',
    transactionAmount: 2000,
    transactionBasicInfo: {
      ...nonMerchantBasicInfo,
      transactionDateTime: '2026-06-08T08:45:00+09:00',
    },
    transactionCancelInfo: {},
    transactionDetailType: 'BANK_PAY_CHARGE',
    transactionId: '260608084500000104',
    transactionType: 'SELF_CHARGE',
  },
  // コード決済 / ハウスプリペイド決済（取引失敗・エラー情報あり・取消ボタン非表示）
  '260608091000000105': {
    adjustmentAmount: 0,
    cancelFlg: false,
    errorCode: 'ERR-D102-0001',
    errorMessage: '残高不足のため決済に失敗しました',
    externalTransactionHistoryList: createExternalTransactionHistoryList('260608091000000105'),
    giftInfo: {},
    giftPurchaseInfo: {
      giftTicketList: [],
    },
    serviceTransactionId: '20260608000000000000000000000105',
    specifiedAmount: 1500,
    status: 'FAILURE',
    transactionAmount: 1500,
    transactionBasicInfo: {
      ...merchantBasicInfo,
      transactionDateTime: '2026-06-08T09:10:00+09:00',
    },
    transactionCancelInfo: {},
    transactionDetailType: 'HOUSE_PREPAID_SETTLEMENT',
    transactionId: '260608091000000105',
    transactionType: 'CODE_PAYMENT',
  },
  // コード決済 / 商品券決済（取引失敗・商品券情報カード＋エラー情報の組み合わせ）
  '260608092000000106': {
    adjustmentAmount: 600,
    cancelFlg: false,
    errorCode: 'ERR-D102-0002',
    errorMessage: '商品券残高不足のため決済に失敗しました',
    externalTransactionHistoryList: emptyExternalTransactionHistoryList,
    giftInfo: {
      bonusValueTransactionAmount: 200,
      giftTicketId: '1',
      giftTicketName: '共通利用券',
      prepaidValueTransactionAmount: 400,
    },
    giftPurchaseInfo: {
      giftTicketList: [],
    },
    serviceTransactionId: '20260608000000000000000000000106',
    specifiedAmount: 600,
    status: 'FAILURE',
    transactionAmount: 0,
    transactionBasicInfo: {
      ...merchantBasicInfo,
      externalUserId: '5fb14db523874dacbcf54934b65dc276',
      giftIssuerId: '1',
      giftIssuerName: 'くまもとデジタル推進協会',
      giftBusinessId: '1',
      giftBusinessName: '熊本市デジタル商品券',
      transactionDateTime: '2026-06-08T09:20:00+09:00',
      walletUserId: '659',
    },
    transactionCancelInfo: {},
    transactionDetailType: 'GIFT_CERTIFICATE_SETTLEMENT',
    transactionId: '260608092000000106',
    transactionType: 'CODE_PAYMENT',
  },
};

export const transactionDetail = http.get(
  createMockEndpoint('/transaction/:id'),
  async ({ params }) => {
    await delay(500);
    const response = responses[params.id.toString()] ?? defaultResponse;
    return HttpResponse.json(response, { status: 200 });
  },
);
